/**
 * Persistent exploration data that survives across deploy/recall cycles.
 * Tracks which tiles have been revealed (fog of war) and visited (by the robot).
 */
export class ExplorationData {
  /** Fog of war — tiles the player can see on the map */
  readonly revealed: boolean[][];
  /** Tiles the robot has "scanned" (within vision radius during any expedition) */
  readonly visited: Set<string>;
  /** Unvisited walkable tiles adjacent to visited ones */
  readonly frontier: Set<string>;
  /** Tiles the pathfinder couldn't reach */
  readonly unreachable: Set<string>;

  readonly mapWidth: number;
  readonly mapHeight: number;
  private _revealedCount = 0;

  // Incremental bounding box of revealed tiles — avoids full grid scan in buildMinimap
  private _bboxMinX: number;
  private _bboxMinY: number;
  private _bboxMaxX: number;
  private _bboxMaxY: number;

  constructor(width: number, height: number) {
    this.mapWidth = width;
    this.mapHeight = height;

    this.revealed = [];
    for (let y = 0; y < height; y++) {
      this.revealed[y] = new Array<boolean>(width).fill(false);
    }

    this._bboxMinX = width;
    this._bboxMinY = height;
    this._bboxMaxX = 0;
    this._bboxMaxY = 0;

    this.visited = new Set();
    this.frontier = new Set();
    this.unreachable = new Set();
  }

  /** Reveal a single tile (safe, no-op if already revealed or out of bounds) */
  revealTile(x: number, y: number) {
    if (x >= 0 && x < this.mapWidth && y >= 0 && y < this.mapHeight && !this.revealed[y]![x]) {
      this.revealed[y]![x] = true;
      this._revealedCount++;
      if (x < this._bboxMinX) this._bboxMinX = x;
      if (x > this._bboxMaxX) this._bboxMaxX = x;
      if (y < this._bboxMinY) this._bboxMinY = y;
      if (y > this._bboxMaxY) this._bboxMaxY = y;
    }
  }

  isRevealed(x: number, y: number): boolean {
    if (x < 0 || x >= this.mapWidth || y < 0 || y >= this.mapHeight) return false;
    return this.revealed[y]![x]!;
  }

  reveal(cx: number, cy: number, radius: number) {
    const ri = Math.ceil(radius);
    for (let dy = -ri; dy <= ri; dy++) {
      for (let dx = -ri; dx <= ri; dx++) {
        if (dx * dx + dy * dy > radius * radius) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < this.mapWidth && y >= 0 && y < this.mapHeight) {
          if (!this.revealed[y]![x]) {
            this.revealed[y]![x] = true;
            this._revealedCount++;
            if (x < this._bboxMinX) this._bboxMinX = x;
            if (x > this._bboxMaxX) this._bboxMaxX = x;
            if (y < this._bboxMinY) this._bboxMinY = y;
            if (y > this._bboxMaxY) this._bboxMaxY = y;
          }
        }
      }
    }
  }

  /** How many tiles have been revealed so far */
  get revealedCount(): number {
    return this._revealedCount;
  }

  /** Bounding box of all revealed tiles (incrementally maintained). */
  get revealedBBox(): { minX: number; minY: number; maxX: number; maxY: number; hasRevealed: boolean } {
    return {
      minX: this._bboxMinX,
      minY: this._bboxMinY,
      maxX: this._bboxMaxX,
      maxY: this._bboxMaxY,
      hasRevealed: this._revealedCount > 0,
    };
  }

  /** Serialize exploration state for localStorage persistence */
  serialize(): { w: number; h: number; rev: string; vis: string[]; fr: string[]; unr: string[] } {
    // Pack revealed[][] into a compact binary string (base64)
    const bits: number[] = [];
    for (let y = 0; y < this.mapHeight; y++) {
      for (let x = 0; x < this.mapWidth; x++) {
        bits.push(this.revealed[y]![x] ? 1 : 0);
      }
    }
    // Pack bits into bytes then base64
    const bytes: number[] = [];
    for (let i = 0; i < bits.length; i += 8) {
      let byte = 0;
      for (let b = 0; b < 8 && i + b < bits.length; b++) {
        if (bits[i + b]) byte |= (1 << b);
      }
      bytes.push(byte);
    }
    const rev = btoa(String.fromCharCode(...bytes));

    return {
      w: this.mapWidth,
      h: this.mapHeight,
      rev,
      vis: Array.from(this.visited),
      fr: Array.from(this.frontier),
      unr: Array.from(this.unreachable),
    };
  }

  /** Restore exploration state from serialized data */
  static deserialize(data: { w: number; h: number; rev: string; vis: string[]; fr: string[]; unr: string[] }): ExplorationData {
    const exp = new ExplorationData(data.w, data.h);

    // Unpack base64 → bytes → bits → revealed[][]
    const raw = atob(data.rev);
    const bytes: number[] = [];
    for (let i = 0; i < raw.length; i++) bytes.push(raw.charCodeAt(i));

    let bitIdx = 0;
    for (let y = 0; y < data.h; y++) {
      for (let x = 0; x < data.w; x++) {
        const byteIdx = Math.floor(bitIdx / 8);
        const bitOff = bitIdx % 8;
        if (byteIdx < bytes.length && (bytes[byteIdx]! & (1 << bitOff))) {
          exp.revealed[y]![x] = true;
          exp._revealedCount++;
          if (x < exp._bboxMinX) exp._bboxMinX = x;
          if (x > exp._bboxMaxX) exp._bboxMaxX = x;
          if (y < exp._bboxMinY) exp._bboxMinY = y;
          if (y > exp._bboxMaxY) exp._bboxMaxY = y;
        }
        bitIdx++;
      }
    }

    for (const v of data.vis) exp.visited.add(v);
    for (const f of data.fr) exp.frontier.add(f);
    for (const u of data.unr) exp.unreachable.add(u);

    return exp;
  }
}
